import {
  ChakraBox,
  ChakraSimpleGrid,
  ChakraFlex,
  Icon,
  Text,
  UniversalSelect,
  FAIconName,
  TextInputV2,
  ChakraImage,
  ChakraGridItem,
  FileIconsV2,
  MultiSelectOption,
} from '@equip.health/ui';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  getExtension,
  getFileIconV2,
  isImage,
} from '~/lib/util/chat/chat.utils';
import fileUtil from '~/lib/util/file.util';
import { getFileKey } from '~/lib/util/patient.util';
import { useGetPatientDocumentTagsQuery } from '~/store/services/enumerations';

const templateColumns = '3fr .8fr 3.3fr 60px 20px';

interface UploadDocumentsListProps {
  files: File[];
  metadata: Record<string, FileMetadata>;
  modalId: string;
  onRemoveFile: (file: File) => void;
  onToggleSensitive: (file: File) => void;
  onMetadataChange: (file: File, data: Partial<FileMetadata>) => void;
}

const UploadDocumentsList = ({
  files,
  metadata,
  modalId,
  onRemoveFile,
  onToggleSensitive,
  onMetadataChange,
}: UploadDocumentsListProps) => {
  const { data: documentTags, isLoading: isGetDocumentTagsLoading } =
    useGetPatientDocumentTagsQuery();
  const { trackEvent } = useAnalytics();
  const getThumbnail = (file: File) => {
    const src = URL.createObjectURL(file);
    return (
      <ChakraImage
        borderRadius="6px"
        height="40px"
        objectFit="cover"
        onLoad={() => URL.revokeObjectURL(src)}
        src={src}
        width="40px"
      />
    );
  };

  const debouncedTrackSearchEvent = useMemo(
    () =>
      debounce((inputValue: string) => {
        trackEvent(DocumentCenterEvents.documentTypeSearchPerformedFromUpload, {
          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
          SearchTerm: inputValue,
        });
      }, 500),
    [trackEvent],
  );

  return (
    <ChakraBox position="relative" width="100%">
      <ChakraSimpleGrid
        alignItems="center"
        background="neutral.background.secondary"
        borderBottom="1px solid"
        borderColor="neutral.line"
        borderRadius="6px"
        color="neutral.text.secondary"
        columnGap="24px"
        padding="10px 12px"
        templateColumns={templateColumns}
      >
        <ChakraGridItem>
          <Text color="neutral.secondary" paddingLeft="52px" variant="caption">
            Name
          </Text>
        </ChakraGridItem>
        <ChakraGridItem>
          <Text color="neutral.secondary" variant="caption">
            File format
          </Text>
        </ChakraGridItem>
        <ChakraGridItem>
          <Text color="neutral.secondary" variant="caption">
            Document type
          </Text>
        </ChakraGridItem>
        <ChakraGridItem>
          <Text color="neutral.secondary" variant="caption">
            Sensitive?
          </Text>
        </ChakraGridItem>
        <ChakraGridItem>
          <Text />
        </ChakraGridItem>
      </ChakraSimpleGrid>

      <ChakraBox
        display="grid"
        gap="15px"
        maxHeight="calc(100vh - 27rem)"
        overflow="auto"
        padding="8px 12px"
      >
        {files.map((file: File, index: number) => {
          const fileKey = getFileKey(file);
          const isImageDocument = isImage(file.name);
          const isSensitiveForFile = metadata[fileKey]?.sensitive ?? false;

          return (
            <ChakraSimpleGrid
              alignItems="center"
              columnGap="24px"
              key={`${file.name}-${index}`}
              rowGap="15px"
              templateColumns={templateColumns}
            >
              <ChakraGridItem>
                <ChakraFlex alignItems="center" gap="10px">
                  {isImageDocument ? (
                    getThumbnail(file)
                  ) : (
                    <ChakraBox height="40px" width="40px">
                      <FileIconsV2
                        height="40px"
                        icon={getFileIconV2(file.name)}
                        width="40px"
                      />
                    </ChakraBox>
                  )}
                  <TextInputV2
                    id={`${file.name}__document-name-input`}
                    onChange={(val: string) =>
                      onMetadataChange(file, {
                        fileName: val,
                      })
                    }
                    onFocus={() => {
                      trackEvent(
                        DocumentCenterEvents.documentNameFieldClickedFromUpload,
                        {
                          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                        },
                      );
                    }}
                    value={
                      metadata[fileKey]?.fileName ??
                      fileUtil.getFileNameWithoutExtension(file.name)
                    }
                  />
                </ChakraFlex>
              </ChakraGridItem>

              <ChakraGridItem>
                <Text>{getExtension(file.name)?.toUpperCase() || '—'}</Text>
              </ChakraGridItem>

              <ChakraGridItem
                display="grid"
                gap="10px"
                position="relative"
                zIndex={files.length - index}
              >
                <UniversalSelect
                  id={`${file.name}__document-type-select`}
                  isLoading={isGetDocumentTagsLoading}
                  isSearchable
                  isSingleSelect={false}
                  leftIcon={FAIconName.magnifyingGlassRegular}
                  minMenuHeight={70}
                  modalId={modalId}
                  onChange={(selected: MultiSelectOption[]) => {
                    const previousTags = metadata[fileKey]?.tags || [];
                    const selectedValues = new Set(
                      selected.map((s) => s.value),
                    );
                    const removedTags = previousTags.filter(
                      (tag) => !selectedValues.has(tag),
                    );
                    if (removedTags.length > 0 && selected.length > 0) {
                      trackEvent(
                        DocumentCenterEvents.documentTypeOptionRemovedFromUpload,
                        {
                          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                          RemovedOption: removedTags[0],
                        },
                      );
                    } else if (!selected || selected.length === 0) {
                      trackEvent(
                        DocumentCenterEvents.documentTypeClearedFromUpload,
                        {
                          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                        },
                      );
                    } else {
                      trackEvent(
                        DocumentCenterEvents.documentTypeOptionClickedFromUpload,
                        {
                          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                          Option: selected[selected.length - 1].value,
                        },
                      );
                    }

                    onMetadataChange(file, {
                      tags: selected.map((s) => s.value),
                    });
                  }}
                  onInputChange={(inputValue: string) => {
                    debouncedTrackSearchEvent(inputValue);
                  }}
                  onMenuOpen={() => {
                    trackEvent(
                      DocumentCenterEvents.documentTypeMenuClickedFromUpload,
                      {
                        Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                      },
                    );
                  }}
                  options={documentTags?.map((tag) => ({
                    ...tag,
                    searchData: tag.label.toLowerCase(),
                  }))}
                  placeholder="Select all that apply"
                  value={(metadata[fileKey]?.tags || [])
                    .map((tagVal: string) => {
                      const tagObj = documentTags?.find(
                        (tag) => tag.value === tagVal,
                      );
                      return tagObj
                        ? { label: tagObj.label, value: tagObj.value }
                        : null;
                    })
                    .filter(Boolean)}
                />
                {index === 0 &&
                  files.length > 1 &&
                  (metadata[fileKey]?.tags?.length ?? 0) > 0 && (
                    <Text
                      color="primary.100"
                      cursor="pointer"
                      marginLeft="40px"
                      onClick={() => {
                        trackEvent(
                          DocumentCenterEvents.documentTypeApplyToAllClickedFromUpload,
                          {
                            Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                          },
                        );
                        const newTags = metadata[fileKey]?.tags ?? [];
                        files.forEach((f) => {
                          onMetadataChange(f, { tags: [...newTags] });
                        });
                      }}
                      variant="bodyMedium"
                    >
                      Apply to all
                    </Text>
                  )}
              </ChakraGridItem>

              <ChakraGridItem
                cursor="pointer"
                display="flex"
                justifyContent="center"
                onClick={() => {
                  trackEvent(
                    !isSensitiveForFile
                      ? DocumentCenterEvents.sensitiveFlaggedFromUpload
                      : DocumentCenterEvents.sensitiveUnflaggedFromUpload,
                    {
                      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                    },
                  );
                  onToggleSensitive(file);
                }}
              >
                <Icon
                  aria-label={
                    isSensitiveForFile
                      ? `Flag sensitive ${index}`
                      : `Unflag sensitive ${index}`
                  }
                  color={
                    isSensitiveForFile ? 'critical.base' : 'neutral.tertiary'
                  }
                  name={
                    isSensitiveForFile
                      ? FAIconName.flagSolid
                      : FAIconName.flagRegular
                  }
                />
              </ChakraGridItem>

              <ChakraGridItem
                cursor="pointer"
                onClick={() => {
                  trackEvent(
                    DocumentCenterEvents.documentRemoveFileClickedFromUpload,
                    {
                      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                    },
                  );
                  onRemoveFile(file);
                }}
              >
                <Icon
                  aria-label={`Remove ${index}`}
                  color="neutral.secondary"
                  name={FAIconName.timesRegular}
                />
              </ChakraGridItem>
            </ChakraSimpleGrid>
          );
        })}
      </ChakraBox>
    </ChakraBox>
  );
};

export default UploadDocumentsList;
